import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  fetchYardsApi, 
  fetchUsersApi, 
  createAutomaticAssignmentApi, 
  fetchOnlyVigilantApi
} from '../../services/api';

export default function AsignacionAutomatica() {
  const { token } = useAuth();
  const [yards, setYards] = useState([]);
  const [users, setUsers] = useState([]);
  const [yardsLoading, setYardsLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    yardId: '',
    userId: '',
    startTime: '08:00',
    endTime: '18:00',
    intervalTime: 30 // en minutos
  });

  // Cargar datos iniciales
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setYardsLoading(true);
        setUsersLoading(true);
        
        const [yardsList, usersList] = await Promise.all([
          fetchYardsApi({ token }),
          fetchOnlyVigilantApi({ token })
        ]);
        
        if (!active) return;
        
        setYards(yardsList);
        setUsers(usersList.filter(user => user.isActive)); // Solo usuarios activos
        
        setYardsLoading(false);
        setUsersLoading(false);
      } catch (err) {
        if (!active) return;
        setYardsLoading(false);
        setUsersLoading(false);
        setError('Error al cargar los datos: ' + err.message);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await createAutomaticAssignmentApi({
        startDate: form.startDate,
        endDate: form.endDate,
        yardId: form.yardId,
        userId: form.userId,
        startTime: form.startTime,
        endTime: form.endTime,
        intervalTime: parseInt(form.intervalTime),
        token
      });
      
      if(response?.isSuccess) {
        setResult(response.value || response);
        // Reiniciar el formulario
        setForm({
          startDate: '',
          endDate: '',
          yardId: '',
          userId: '',
          startTime: '08:00',
          endTime: '18:00',
          intervalTime: 30
        });
      } else {
        
        throw new Error(response?.error || response?.message || 'Error desconocido');
      }
    } catch (err) {
      setError(err.message || 'Error al crear la asignación automática');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      startDate: '',
      endDate: '',
      yardId: '',
      userId: '',
      startTime: '08:00',
      endTime: '18:00',
      intervalTime: 30
    });
    setResult(null);
    setError(null);
  };

  return (
    <div className="container-fluid">
      <div className="mb-2">
        <h2 className="mb-2 text-center">Asignación Automática de Rondas</h2>
      </div>
      
      <div className="row">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>Parámetros de Asignación</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Patio</label>
                  {yardsLoading ? (
                    <div className="form-control-plaintext">Cargando...</div>
                  ) : (
                    <select 
                      className="form-select" 
                      value={form.yardId} 
                      onChange={(e) => setForm({ ...form, yardId: e.target.value })} 
                      required
                      disabled={loading}
                    >
                      <option value="">Seleccionar patio...</option>
                      {yards.map((yard) => (
                        <option key={yard.id} value={yard.id}>{yard.nombre}</option>
                      ))}
                    </select>
                  )}
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Personal</label>
                  {usersLoading ? (
                    <div className="form-control-plaintext">Cargando...</div>
                  ) : (
                    <select 
                      className="form-select" 
                      value={form.userId} 
                      onChange={(e) => setForm({ ...form, userId: e.target.value })} 
                      required
                      disabled={loading}
                    >
                      <option value="">Seleccionar personal...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.nombres} {user.apellidos} ({user.empresaId ? 'Activo' : 'Inactivo'})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Fecha Inicio</label>
                      <input 
                        type="date" 
                        className="form-control" 
                        value={form.startDate} 
                        onChange={(e) => setForm({ ...form, startDate: e.target.value })} 
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Fecha Fin</label>
                      <input 
                        type="date" 
                        className="form-control" 
                        value={form.endDate} 
                        onChange={(e) => setForm({ ...form, endDate: e.target.value })} 
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Hora Inicio Turno</label>
                      <input 
                        type="time" 
                        className="form-control" 
                        value={form.startTime} 
                        onChange={(e) => setForm({ ...form, startTime: e.target.value })} 
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Hora Fin Turno</label>
                      <input 
                        type="time" 
                        className="form-control" 
                        value={form.endTime} 
                        onChange={(e) => setForm({ ...form, endTime: e.target.value })} 
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Intervalo entre rondas (minutos)</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    value={form.intervalTime} 
                    onChange={(e) => setForm({ ...form, intervalTime: e.target.value })} 
                    min="5" 
                    max="120"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button 
                    type="button" 
                    className="btn btn-secondary me-md-2" 
                    onClick={resetForm}
                    disabled={loading}
                  >
                    Limpiar
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading || yardsLoading || usersLoading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Procesando...
                      </>
                    ) : 'Crear Asignación Automática'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>Resultado</h5>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}
              
              {result && (
                <div className="alert alert-success" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <h6>Asignación automática completada exitosamente</h6>
                  <p className="mb-0">
                    {result?.message || 'La asignación automática se completó correctamente.'}
                  </p>
                  {result?.assignmentsCreated && (
                    <p className="mb-0 mt-1">
                      <strong>Asignaciones creadas:</strong> {result.assignmentsCreated}
                    </p>
                  )}
                </div>
              )}
              
              <div className="mt-4">
                <h6>Instrucciones:</h6>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Selecciona el patio y el personal para asignar
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Define el rango de fechas para la asignación
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Especifica las horas de inicio y fin del turno
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    Configura el intervalo entre rondas (en minutos)
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle text-success me-2"></i>
                    El sistema generará automáticamente las rondas dentro del rango de fechas y horarios especificados
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}